// frontend/src/components/BulkSkillInputModal.jsx
// 経験録の一括入力モーダル
//
// - 縦: 診療科ごとにグルーピング + 各術式
// - 横: 術式ごとに定義された役割 (器械出し / 外回り 等)
// - セル: 8 段階の経験レベルを compact ラジオボタンで
// - 保存で変更のあった全項目を一括 upsert

import React, { useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { writeClientAudit } from '../utils/auditClient';

// Edge Function 経由ではなくクライアントから直接 skill_records を書き込む保存ロジック。
// Edge Function (save-skill-records) の未デプロイで保存できなくなる問題を回避するため。
// (ProcedureDetailModal の単体保存と同じスタイル、ただし複数レコード対応)
async function saveSkillsClientDirect(records, organizationId, evaluatorName = 'bulk') {
  const today = new Date().toISOString().split('T')[0];
  let okCount = 0;
  const errors = [];
  for (const r of records) {
    try {
      const userId = String(r.user_id);
      const procId = String(r.procedure_id);
      const role = String(r.role);
      const label = r.label || null;
      const level = String(r.level);

      // 既存検索
      //   label の分岐:
      //     - null  → label IS NULL + label = '' の両方を候補にする (旧データ救済)
      //     - その他 → label = <値>
      //   マイグレーション起因の重複行 (同じ user+proc+role で 2 行以上) に備え
      //   .maybeSingle() ではなく order+limit(1) で先頭 1 行を取る。
      let findQ = supabase
        .from('skill_records')
        .select('id, evaluation_history')
        .eq('organization_id', organizationId)
        .eq('user_id', userId)
        .eq('procedure_id', procId)
        .eq('role', role);
      if (label === null) {
        findQ = findQ.or('label.is.null,label.eq.');
      } else {
        findQ = findQ.eq('label', label);
      }
      findQ = findQ.order('id', { ascending: true }).limit(1);
      const { data: existingList, error: findErr } = await findQ;
      if (findErr) throw findErr;
      const existing = Array.isArray(existingList) ? existingList[0] : null;

      const historyEntry = { level, evaluated_at: today, evaluated_by: evaluatorName };

      if (existing?.id) {
        const prevHist = Array.isArray(existing.evaluation_history) ? existing.evaluation_history : [];
        const { error: upErr } = await supabase
          .from('skill_records')
          .update({ level, evaluation_history: [...prevHist, historyEntry] })
          .eq('id', existing.id);
        if (upErr) throw upErr;
      } else {
        const insertRec = {
          organization_id: organizationId,
          user_id: userId,
          procedure_id: procId,
          role,
          level,
          evaluation_history: [historyEntry],
        };
        if (label !== null) insertRec.label = label;
        const { error: insErr } = await supabase.from('skill_records').insert(insertRec);
        if (insErr) throw insErr;
      }
      okCount++;
    } catch (e) {
      errors.push(`${r.procedure_id}/${r.role}: ${e?.message || e}`);
    }
  }
  return { okCount, errors };
}

const STATUS_OPTIONS = [
  '未経験', '見学済み', '経験1回', '経験2回',
  '経験3回以上', '外から見守り', '独り立ち', '指導可',
];
const STATUS_COLORS = {
  '未経験': '#e5e7eb',
  '見学済み': '#fed7aa',
  '経験1回': '#fdba74',
  '経験2回': '#fb923c',
  '経験3回以上': '#f97316',
  '外から見守り': '#86efac',
  '独り立ち': '#4ade80',
  '指導可': '#22c55e',
};

// キー生成: procedure_id + role + label
const keyOf = (procId, roleType, roleLabel) =>
  roleLabel ? `${procId}__${roleType}__${roleLabel}` : `${procId}__${roleType}`;

const BulkSkillInputModal = ({
  isOpen,
  onClose,
  staffId,
  organizationId,
  proceduresByDept, // { deptName: [{ id, name, requiredRoles, skills }] }
  onSaved,
}) => {
  // 変更差分: key → level
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterDept, setFilterDept] = useState('all');

  const deptNames = useMemo(() => Object.keys(proceduresByDept || {}), [proceduresByDept]);

  // 現在の全 (key → level): 変更 → 既存の順で参照する
  const currentLevelOf = (procId, roleType, roleLabel, existing) => {
    const k = keyOf(procId, roleType, roleLabel);
    return changes[k] !== undefined ? changes[k] : (existing || '未経験');
  };

  const setLevel = (procId, roleType, roleLabel, level) => {
    setChanges((prev) => ({ ...prev, [keyOf(procId, roleType, roleLabel)]: level }));
  };

  const changeCount = Object.keys(changes).length;

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (changeCount === 0) {
      setError('変更がありません');
      return;
    }
    if (!staffId || !organizationId) {
      setError('スタッフまたは組織情報が不足しています');
      return;
    }
    setSaving(true);
    try {
      const records = Object.entries(changes).map(([key, level]) => {
        const parts = key.split('__');
        // procedure_id__role__label? (label がある時のみ 3 要素)
        const record = {
          user_id: String(staffId),
          procedure_id: parts[0],
          role: parts[1],
          level,
          organization_id: organizationId,
        };
        if (parts[2]) record.label = parts[2];
        return record;
      });
      const { okCount, errors } = await saveSkillsClientDirect(records, organizationId);
      if (okCount > 0) {
        setSuccess(
          errors.length > 0
            ? `${okCount} 件を保存しました (${errors.length} 件失敗)`
            : `${okCount} 件を保存しました`
        );
        setChanges({});
        if (onSaved) onSaved();
      }
      await writeClientAudit({
        organizationId,
        actionType: 'update',
        targetTable: 'skill_records',
        targetId: String(staffId),
        targetSummary: `経験録一括入力: 成功 ${okCount} 件${errors.length ? ` / 失敗 ${errors.length} 件` : ''} (対象スタッフ=${staffId})`,
        changes: { staffId, processed: okCount, failed: errors.length },
        success: okCount > 0,
        errorMessage: errors.length ? errors.slice(0, 2).join(' / ') : null,
      });
      if (errors.length > 0 && okCount === 0) {
        setError(`保存に失敗しました: ${errors.slice(0, 2).join(' / ')}`);
      } else if (errors.length > 0) {
        console.warn('一部失敗:', errors);
      }
    } catch (e) {
      console.error('一括保存失敗:', e);
      await writeClientAudit({
        organizationId,
        actionType: 'update',
        targetTable: 'skill_records',
        targetId: String(staffId),
        targetSummary: `経験録一括入力でエラー (対象スタッフ=${staffId})`,
        success: false,
        errorMessage: e.message,
      });
      setError(`保存エラー: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const applyToColumn = (roleType, level) => {
    // その診療科の全術式でロール一致する行に一括適用
    if (!window.confirm(`表示中の${filterDept === 'all' ? '全' : filterDept}の術式で「${roleType}」を全て「${level}」に設定しますか?`)) return;
    const patch = {};
    const targetDepts = filterDept === 'all' ? deptNames : [filterDept];
    targetDepts.forEach((deptName) => {
      (proceduresByDept[deptName] || []).forEach((proc) => {
        (proc.requiredRoles || []).forEach((r) => {
          if (r.type !== roleType) return;
          patch[keyOf(proc.id, r.type, r.label)] = level;
        });
      });
    });
    setChanges((prev) => ({ ...prev, ...patch }));
  };

  if (!isOpen) return null;

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        {/* ヘッダー */}
        <div style={header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📝 経験録 一括入力</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
              変更したセルだけが保存対象になります (未変更は影響なし)。
            </p>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* ツールバー */}
        <div style={toolbar}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#64748b' }}>
              診療科:{' '}
              <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} style={selectStyle}>
                <option value="all">全て</option>
                {deptNames.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              一括適用:
              <button onClick={() => applyToColumn('scrub', '未経験')} style={quickBtn}>器械出しを未経験に</button>
              <button onClick={() => applyToColumn('circulating', '未経験')} style={quickBtn}>外回りを未経験に</button>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {error && <span style={{ color: '#dc2626', fontSize: 12 }}>{error}</span>}
            {success && <span style={{ color: '#16a34a', fontSize: 12 }}>{success}</span>}
            <span style={{ fontSize: 12, color: '#64748b' }}>変更: <strong>{changeCount}</strong> 件</span>
            <button onClick={() => setChanges({})} disabled={saving || changeCount === 0} style={{
              ...secondaryBtn,
              opacity: (saving || changeCount === 0) ? 0.4 : 1,
              cursor: (saving || changeCount === 0) ? 'not-allowed' : 'pointer',
            }}>
              変更を破棄
            </button>
            <button onClick={handleSave} disabled={saving || changeCount === 0} style={{
              ...primaryBtn,
              opacity: (saving || changeCount === 0) ? 0.4 : 1,
              cursor: (saving || changeCount === 0) ? 'not-allowed' : 'pointer',
            }}>
              {saving ? '保存中...' : `💾 ${changeCount} 件を保存`}
            </button>
          </div>
        </div>

        {/* 凡例 */}
        <div style={legendBar}>
          {STATUS_OPTIONS.map((s) => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: STATUS_COLORS[s] }} />
              {s}
            </span>
          ))}
        </div>

        {/* テーブル */}
        <div style={tableWrap}>
          {deptNames
            .filter((d) => filterDept === 'all' || d === filterDept)
            .map((deptName) => {
              const procs = proceduresByDept[deptName] || [];
              if (procs.length === 0) return null;
              return (
                <div key={deptName} style={{ marginBottom: 20 }}>
                  <div style={deptHeader}>{deptName} ({procs.length}件)</div>
                  <table style={table}>
                    <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 2 }}>
                      <tr>
                        <th style={{ ...th, minWidth: 200, textAlign: 'left', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 3 }}>
                          術式
                        </th>
                        <th style={th}>役割</th>
                        <th style={{ ...th, minWidth: 480 }}>経験レベル</th>
                      </tr>
                    </thead>
                    <tbody>
                      {procs.flatMap((proc) => {
                        const roles = (proc.requiredRoles || []).length > 0
                          ? proc.requiredRoles
                          : [{ type: 'scrub' }, { type: 'circulating' }]; // フォールバック
                        return roles.map((role, ri) => {
                          const roleLabel = role.label || null;
                          const roleDisplay = role.label
                            ? `${role.type === 'scrub' ? '器械出し' : role.type === 'circulating' ? '外回り' : role.type}(${role.label})`
                            : role.type === 'scrub' ? '器械出し' : role.type === 'circulating' ? '外回り' : role.type;
                          const rk = role.label
                            ? `${role.type}_${role.label}`
                            : role.type;
                          const existingLevel = proc.skills?.[rk];
                          const cur = currentLevelOf(proc.id, role.type, roleLabel, existingLevel);
                          const isChanged = changes[keyOf(proc.id, role.type, roleLabel)] !== undefined;
                          return (
                            <tr key={`${proc.id}-${ri}`} style={{ borderBottom: '1px solid #f1f5f9', background: isChanged ? '#fefce8' : 'transparent' }}>
                              {ri === 0 && (
                                <td rowSpan={roles.length} style={{ ...td, textAlign: 'left', fontWeight: 600, verticalAlign: 'top', position: 'sticky', left: 0, background: 'white' }}>
                                  {proc.name}
                                </td>
                              )}
                              <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{roleDisplay}</td>
                              <td style={{ ...td, padding: '6px 8px' }}>
                                <div style={radioGroup}>
                                  {STATUS_OPTIONS.map((opt) => {
                                    const active = cur === opt;
                                    return (
                                      <label key={opt} style={{
                                        ...radioLabel,
                                        background: active ? STATUS_COLORS[opt] : 'white',
                                        color: active ? '#0f172a' : '#64748b',
                                        borderColor: active ? '#0f172a' : '#e5e7eb',
                                        fontWeight: active ? 700 : 400,
                                      }}>
                                        <input
                                          type="radio"
                                          name={`skill-${proc.id}-${role.type}-${role.label || ''}`}
                                          value={opt}
                                          checked={active}
                                          onChange={() => setLevel(proc.id, role.type, roleLabel, opt)}
                                          style={{ display: 'none' }}
                                        />
                                        {opt}
                                      </label>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

// スタイル
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10000, padding: 16,
  backdropFilter: 'blur(6px)',
};
const modal = {
  background: 'white', borderRadius: 16, width: '100%', maxWidth: 1200,
  maxHeight: '92vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 64px -16px rgba(15,23,42,0.35)',
  overflow: 'hidden',
};
const header = { padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' };
const closeBtn = { background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' };
const toolbar = {
  padding: '10px 20px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
};
const legendBar = {
  padding: '8px 20px', borderBottom: '1px solid #e5e7eb',
  display: 'flex', flexWrap: 'wrap', gap: 12, background: '#fafbfc',
};
const tableWrap = { padding: 16, overflow: 'auto', flex: 1 };
const deptHeader = {
  fontSize: 14, fontWeight: 700, color: '#1e40af',
  padding: '6px 10px', background: '#eff6ff',
  borderRadius: 6, marginBottom: 6,
};
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 11, borderBottom: '2px solid #e5e7eb' };
const td = { padding: '6px 10px', textAlign: 'center' };
const radioGroup = { display: 'flex', gap: 4, flexWrap: 'nowrap' };
const radioLabel = {
  padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e7eb',
  fontSize: 11, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  transition: 'all 0.1s',
};
const selectStyle = { padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 };
const quickBtn = { marginLeft: 6, padding: '3px 8px', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, cursor: 'pointer' };
const primaryBtn = { padding: '8px 16px', background: 'linear-gradient(135deg, #1d4ed8, #1e3a8a)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13 };
const secondaryBtn = { padding: '8px 14px', background: '#e5e7eb', color: '#334155', border: 'none', borderRadius: 8, fontSize: 13 };

export default BulkSkillInputModal;
